/**
 * Etapa 2 — Gastos com deslocamento (mockup 20 Estado B).
 *
 * Bloco mais denso do legacy IBIOMA: 3 combustíveis com unit-toggle +
 * toggle "considerar público" (6 % modais + distância média) + toggle
 * "considerar fornecedores" (lista dinâmica).
 *
 * Style alinhado com mockup screen 20.
 */
import type { EventosOrgInputs, SupplierLine } from '../api/types';
import { t, type I18nBundle } from '../lib/i18n';
import { MetricWithUnitToggle } from './MetricWithUnitToggle';
import { NavRow, QBlock } from './QBlock';
import { Slider } from './Slider';

interface Props {
  value: EventosOrgInputs;
  onChange: (patch: Partial<EventosOrgInputs>) => void;
  onBack: () => void;
  onNext: () => void;
  i18n: I18nBundle;
}

export function StepOrgDeslocamento({ value, onChange, onBack, onNext, i18n }: Props) {
  const totalPct =
    value.pct_public +
    value.pct_private +
    value.pct_walking +
    value.pct_short_flight +
    value.pct_medium_flight +
    value.pct_long_flight;
  const pctOverflow = value.consider_audience && totalPct > 100;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
      className="flex flex-col gap-4"
    >
      <QBlock title={t(i18n, 'org.step2.title')}>
        <MetricWithUnitToggle
          label={t(i18n, 'org.step2.fuel_internal')}
          hint={t(i18n, 'org.step2.fuel_internal_hint')}
          value={value.internal_fuel}
          units={['BRL', 'km', 'L']}
          onChange={(v) => onChange({ internal_fuel: v })}
        />
        <MetricWithUnitToggle
          label={t(i18n, 'org.step2.fuel_employee_private')}
          hint={t(i18n, 'org.step2.fuel_employee_private_hint')}
          value={value.employee_private_fuel}
          units={['BRL', 'km', 'L']}
          onChange={(v) => onChange({ employee_private_fuel: v })}
        />
        {/* Público: # representa número total de viagens (não combustível em
          litros — em ônibus/metrô quem queima é a operadora). */}
        <MetricWithUnitToggle
          label={t(i18n, 'org.step2.fuel_employee_public')}
          hint={t(i18n, 'org.step2.fuel_employee_public_hint')}
          value={value.employee_public_fuel}
          units={['BRL', 'km', 'trips']}
          onChange={(v) => onChange({ employee_public_fuel: v })}
        />
      </QBlock>

      <QBlock>
        <YesNoRow
          label={t(i18n, 'org.step2.consider_audience')}
          value={value.consider_audience}
          onChange={(v) => onChange({ consider_audience: v })}
          yesLabel={t(i18n, 'org.common.yes')}
          noLabel={t(i18n, 'org.common.no')}
        />

        {value.consider_audience && (
          <div className="mt-2">
            <Slider
              label={t(i18n, 'org.step2.audience_avg_km')}
              value={value.audience_avg_km}
              onChange={(v) => onChange({ audience_avg_km: v })}
              min={0}
              max={2000}
              step={1}
              unit={t(i18n, 'org.units.km')}
            />
            <Slider
              label={t(i18n, 'org.step2.pct_public')}
              value={value.pct_public}
              onChange={(v) => onChange({ pct_public: v })}
              asPercent
            />
            <Slider
              label={t(i18n, 'org.step2.pct_private')}
              value={value.pct_private}
              onChange={(v) => onChange({ pct_private: v })}
              asPercent
            />
            <Slider
              label={t(i18n, 'org.step2.pct_walking')}
              value={value.pct_walking}
              onChange={(v) => onChange({ pct_walking: v })}
              asPercent
            />
            <Slider
              label={t(i18n, 'org.step2.pct_short_flight')}
              value={value.pct_short_flight}
              onChange={(v) => onChange({ pct_short_flight: v })}
              asPercent
            />
            <Slider
              label={t(i18n, 'org.step2.pct_medium_flight')}
              value={value.pct_medium_flight}
              onChange={(v) => onChange({ pct_medium_flight: v })}
              asPercent
            />
            <Slider
              label={t(i18n, 'org.step2.pct_long_flight')}
              value={value.pct_long_flight}
              onChange={(v) => onChange({ pct_long_flight: v })}
              asPercent
            />

            <p
              className={
                'text-xs mt-3 font-mono ' +
                (pctOverflow ? 'text-cobre-700 font-semibold' : 'text-ink-subtle')
              }
            >
              {t(i18n, 'org.step2.pct_sum_prefix')} <strong>{totalPct.toFixed(0)}%</strong>
              {pctOverflow && ` — ${t(i18n, 'org.step2.pct_overflow')}`}
            </p>
          </div>
        )}
      </QBlock>

      <QBlock>
        <YesNoRow
          label={t(i18n, 'org.step2.consider_suppliers')}
          value={value.consider_suppliers}
          onChange={(v) => onChange({ consider_suppliers: v })}
          yesLabel={t(i18n, 'org.common.yes')}
          noLabel={t(i18n, 'org.common.no')}
        />

        {value.consider_suppliers && (
          <div className="mt-3 flex flex-col gap-2.5">
            {value.suppliers.length === 0 && (
              <p className="text-xs text-ink-subtle font-mono">
                {t(i18n, 'org.step2.suppliers_empty')}
              </p>
            )}
            {value.suppliers.map((s, idx) => (
              <SupplierRow
                key={idx}
                supplier={s}
                onChange={(patch) => {
                  const copy: SupplierLine[] = value.suppliers.map((row, i) =>
                    i === idx ? { ...row, ...patch } : row,
                  );
                  onChange({ suppliers: copy });
                }}
                onRemove={() => {
                  const copy = value.suppliers.filter((_, i) => i !== idx);
                  onChange({ suppliers: copy });
                }}
                placeholder={t(i18n, 'org.step2.supplier_name_placeholder')}
                removeAria={t(i18n, 'org.step2.supplier_remove_aria')}
                kmUnit={t(i18n, 'org.units.km')}
              />
            ))}
            <button
              type="button"
              className="w-full py-3.5 mt-3 bg-transparent border-[1.5px] border-dashed border-areia rounded-xl font-display font-semibold text-[11.5px] uppercase tracking-[0.18em] text-cobre hover:border-cobre hover:bg-cobre/5 transition-colors"
              onClick={() =>
                onChange({
                  suppliers: [...value.suppliers, { name: '', distance_km: 0 }],
                })
              }
            >
              {t(i18n, 'org.step2.suppliers_add')}
            </button>
          </div>
        )}
      </QBlock>

      <NavRow
        nextIsSubmit
        onBack={onBack}
        backLabel={t(i18n, 'org.common.back')}
        nextLabel={t(i18n, 'org.common.next')}
        nextDisabled={pctOverflow}
      />
    </form>
  );
}

function YesNoRow({
  label,
  value,
  onChange,
  yesLabel,
  noLabel,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}) {
  return (
    <div className="py-2">
      <p className="text-[14px] text-ink leading-snug mb-3">{label}</p>
      <div className="flex gap-8">
        <RadioOption
          checked={value === true}
          onClick={() => onChange(true)}
          label={yesLabel}
        />
        <RadioOption
          checked={value === false}
          onClick={() => onChange(false)}
          label={noLabel}
        />
      </div>
    </div>
  );
}

function RadioOption({
  checked,
  onClick,
  label,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className="inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer hover:text-floresta transition-colors"
    >
      <span
        className={
          'inline-block size-5 rounded-full border-2 relative transition-colors ' +
          (checked ? 'border-floresta' : 'border-areia')
        }
      >
        {checked && (
          <span aria-hidden className="absolute inset-[3px] rounded-full bg-floresta" />
        )}
      </span>
      {label}
    </button>
  );
}

function SupplierRow({
  supplier,
  onChange,
  onRemove,
  placeholder,
  removeAria,
  kmUnit,
}: {
  supplier: SupplierLine;
  onChange: (patch: Partial<SupplierLine>) => void;
  onRemove: () => void;
  placeholder: string;
  removeAria: string;
  kmUnit: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_120px_36px_36px] gap-2.5 items-center">
      <input
        type="text"
        value={supplier.name ?? ''}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={placeholder}
        className="rounded-lg border border-areia bg-cream/60 px-3 py-2 text-sm text-ink focus:outline-none focus:border-bronze focus:bg-cream"
      />
      <input
        type="number"
        inputMode="decimal"
        value={supplier.distance_km}
        min={0}
        step={1}
        onChange={(e) => onChange({ distance_km: Number(e.target.value) || 0 })}
        className="rounded-lg border border-areia bg-cream/60 px-3 py-2 text-sm font-mono text-floresta text-right tabular-nums focus:outline-none focus:border-bronze focus:bg-cream"
      />
      <span className="font-mono text-xs text-tabaco text-center">{kmUnit}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeAria}
        className="size-9 inline-flex items-center justify-center rounded-md text-tabaco hover:text-cobre hover:bg-cobre/5 text-xl leading-none"
      >
        ×
      </button>
    </div>
  );
}
